import React, { useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'design-system/styles';
import { Paper } from 'design-system/Paper';
import {
  ChatMockType,
  TextCollectionAttachment,
  TextCollectionAttachmentType,
} from '@scale/shared/types/params/textcollection';
import GView from './GView';
import PanZoomImage from './PanZoomImage';
import { BoundingBox } from './ocr';
import SanitizedMarkdown from 'design-system/SanitizedMarkdown';
import { useViewAttachmentLinter } from './viewAttachmentLinter';
import Video from 'react-scale/components/scalers/common/task-attachment/video';
import Attachment from 'react-scale/components/scalers/common/task-attachment/index';
import { BoundingBox as BoundingBoxFieldType } from '@scale/shared/types/response/textcollection';
import ProtectedTextAttachment from 'react-scale/components/scalers/tasks/textcollection/ProtectedTextAttachment';
import CategorizationEmbeddedTaskResponse from 'react-scale/components/scalers/tasks/categorization/CategorizationEmbeddedTaskResponse';
import TranscriptionAudioAttachmentView from './TranscriptionAudioAttachmentView';
import FrameView from './FrameView';
import DownloadableTextAttachmentView from './DownloadableTextAttachmentView';
import DownloadableEmbeddedAppView from './DownloadableEmbeddedAppView';
import AudioAttachmentView from './AudioAttachmentView';
import useUnleashFeatureFlag, { useUnleashBoolean } from 'react-scale/hooks/useUnleashFeatureFlag';
import { ConfigJson, ReviewLevel } from '@scale/shared/defaults';
import usePinchPan from 'react-scale/hooks/usePinchPan';
import VideoComparison from '../../common/task-attachment/videoComparison';
import { LiveInstructionSection } from '@scale/shared/types/params/genericdatacollection';
import VideoWithLiveInstructions from 'react-scale/components/scalers/common/task-attachment/videoWithLiveInstructions';
import ChatBotView from './ChatbotRanking/ChatBotView';
import ChatBotViewV2 from './ChatbotRanking/ChatBotViewV2';
import { useChatbotContext } from 'react-scale/dashboard.scale.com/contexts/ChatbotRankingContext';
import { SmartAssistantRlhfHook } from 'react-scale/components/scalers/useSmartAssistantRlhf';
// eslint-disable-next-line restrict-imports/restrict-imports
import { GridList, GridListTile } from '@material-ui/core';
import { TextCollectionAnnotationsOrCallback } from '@scale/shared/types/textCollection';
import { Breadcrumb } from './breadcrumbs';
import { useLLMHelper } from '../../../../dashboard.scale.com/contexts/LLMHelperContext';
import PairwiseEvaluation from 'react-scale/components/scalers/tasks/textcollection/PairwiseEvaluation';
import BlurBrightnessImage from 'react-scale/components/scalers/tasks/textcollection/BlurBrightnessImage';
import RLHFPairwiseEvaluation from 'react-scale/components/scalers/tasks/textcollection/RLHFPairwiseEvaluation';
import PDFView from './PDFView';
import { populateTemplateHelper } from '@scale/shared/taskTemplate/helpers';
import { useAsync } from 'react-use-v13';
import VideoWithGrid from 'react-scale/components/scalers/tasks/textcollection/VideoWithGrid';
import {
  BulbaChatMobile,
  BulbaChatDesktop,
  ChatGPTDesktop,
} from 'react-scale/components/scalers/tasks/textcollection/ChatMock';
import _ from 'lodash';

const noop = () => {};

interface AttachmentViewProps {
  attachment: TextCollectionAttachment;
  index: number;
  size: { width: number; height: number };
  hidden?: boolean;
  stacked?: boolean;
  boxes: BoundingBox[];
  editBoundingBoxMode?: boolean;
  boundingBoxField?: BoundingBoxFieldType;
  updateBoundingBoxField?: (bbox: BoundingBoxFieldType) => void;
  loggedUserId?: string;
  useProtectedText?: boolean;
  markAttachmentAsCompleted: (isComplete: boolean) => void;
  completed: boolean;
  allowVideoAttachmentDownload?: boolean;
  projectId?: string;
  projectConfig?: Partial<ConfigJson>;
  isAuditMode?: boolean;
  maximumExtraPlayingTime?: number; // Config for videos
  showSensitiveContentWarning?: boolean;
  handleSkip?: (skipReason: string, skipExplanation: string) => void;
  startSelfDestructSequence?: (secs: number) => void;
  handleLinkClickHistory?: (link: string) => void;
  handleAttachmentFocus: (index: number) => void;
  handleAttachmentBlur: (index: number) => void;
  unfocused: boolean;
  disableVideoZoom?: boolean;
  liveInstructions?: LiveInstructionSection[];
  taskCustomer?: string;
  isSticky?: boolean;
  smartAssistantRlhfHook: SmartAssistantRlhfHook;
  prefillPrompt?: string;
  prefillRefText?: string;
  assignmentId?: string;
  subtaskMetadata?: any;
  setAnnotations?: (
    annotations: TextCollectionAnnotationsOrCallback,
    breadcrumbs?: Breadcrumb[],
  ) => void;
  isReview?: boolean;
  reviewLevel?: ReviewLevel;
}

export default function AttachmentView(props: AttachmentViewProps) {
  const styles = useAttachmentViewStyles();
  const { onViewAttachment } = useViewAttachmentLinter();
  // Disables href & markdown links for sensitive projects.
  const disableLinksInTextAttachment = useUnleashBoolean(
    'c-l-disable-links-in-attachment',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const useGridVideo = useUnleashBoolean(
    'use-grid-video',
    { projectId: props.projectId },
    {
      shortCircuit: !props.projectId,
      defaultValue: false,
    },
  );

  const canStopStreaming = props.taskCustomer === '61d74737b419c3002a50e31a';

  const addBrightnessSlider = props.projectConfig?.imageAttachmentConfig?.addBrightnessSlider;
  const addBlurSlider = props.projectConfig?.imageAttachmentConfig?.addBlurSlider;
  const addZoomSlider = props.projectConfig?.imageAttachmentConfig?.addZoomSlider;

  // These initial states are for video zoom in, they will be updated when the video is loaded.
  const { viewbox, setViewport, handleWheel, setInitialViewport } = usePinchPan(
    { left: 0, top: 0, height: 1, width: 1 },
    {
      autofocusOnZoom: false,
      enableUnfocusedZoom: true,
      zoomOnAlt: true,
    },
  );

  const [dimensions, setDimensions] = React.useState({
    height: 1,
    width: 1,
  });

  const videoContainer = React.useRef<HTMLDivElement>(null);

  const handleResize = () => {
    setViewport({
      left: 0,
      top: 0,
      width: videoContainer.current!.clientWidth,
      height: videoContainer.current!.clientHeight,
    });
    setInitialViewport({
      left: 0,
      top: 0,
      width: videoContainer.current!.clientWidth,
      height: videoContainer.current!.clientHeight,
    });
    setDimensions({
      width: videoContainer.current!.clientWidth,
      height: videoContainer.current!.clientHeight,
    });
  };

  // Fixed: Record if the attachment has been visited.
  React.useEffect(() => {
    if (!props.hidden) {
      onViewAttachment(props.index);
      if (props.attachment.type === TextCollectionAttachmentType.Video) {
        handleResize();
        window.addEventListener('resize', handleResize, false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.index, props.hidden]);

  const onFocus = () => {
    props.handleAttachmentFocus(props.index);
  };
  const onBlur = () => {
    props.handleAttachmentBlur(props.index);
  };

  const chatbotContext = useChatbotContext();
  const llmHelper = useLLMHelper();

  React.useEffect(() => {
    if (props.taskCustomer) {
      chatbotContext.setTaskCustomer(props.taskCustomer);
      llmHelper.setTaskCustomer(props.taskCustomer);
    }
  }, [props.taskCustomer]);

  // eslint-disable-next-line complexity
  const renderType = () => {
    if (chatbotContext.enabled) {
      return chatbotContext.useChatbotV2 ? (
        <ChatBotViewV2
          attachment={props.attachment}
          projectId={props.projectId}
          assignmentId={props.assignmentId}
          subtaskMetadata={props.subtaskMetadata}
          setAnnotations={props.setAnnotations}
          prefillPrompt={props.prefillPrompt}
          prefillRefText={props.prefillRefText}
          canStopStreaming={canStopStreaming}
        />
      ) : (
        <ChatBotView
          attachment={props.attachment}
          projectId={props.projectId}
          smartAssistantRlhfHook={props.smartAssistantRlhfHook}
          assignmentId={props.assignmentId}
          subtaskMetadata={props.subtaskMetadata}
          setAnnotations={props.setAnnotations}
          prefillPrompt={props.prefillPrompt}
          prefillRefText={props.prefillRefText}
        />
      );
    }
    if (props.editBoundingBoxMode && props.attachment.jpegContent) {
      return (
        <PanZoomImage
          boxes={[]}
          disabled={false}
          size={props.size}
          src={props.attachment.jpegContent!}
          editBoundingBoxMode={props.editBoundingBoxMode}
          boundingBoxField={props.boundingBoxField}
          updateBoundingBoxField={props.updateBoundingBoxField}
        />
      );
    }
    switch (props.attachment.type) {
      case TextCollectionAttachmentType.DownloadableText:
        return (
          <DownloadableTextAttachmentView
            attachmentUrl={props.attachment.content}
            useProtectedText={props.useProtectedText}
            loggedUserId={props.loggedUserId}
            renderAsPlainText={props.projectConfig?.textAttachmentConfig?.renderAsPlainText}
            disableLinksInTextAttachment={disableLinksInTextAttachment}
            projectId={props.projectId}
            showSensitiveContentWarning={props.showSensitiveContentWarning}
            handleSkip={props.handleSkip}
          />
        );
      case TextCollectionAttachmentType.DownloadableEmbeddedApp:
        return (
          <DownloadableEmbeddedAppView
            attachmentUrl={props.attachment.content}
            isAuditMode={props.isAuditMode}
          />
        );
      case TextCollectionAttachmentType.Text:
        return (
          <Paper className={styles.textAttachment}>
            <TextComponent
              props={props}
              disableLinksInTextAttachment={disableLinksInTextAttachment}
            />
          </Paper>
        );
      case TextCollectionAttachmentType.Image: {
        if (addBrightnessSlider || addBlurSlider || addZoomSlider) {
          return (
            <BlurBrightnessImage
              src={props.attachment.content}
              caption={props.attachment.caption}
              addBrightnessSlider={addBrightnessSlider}
              addBlurSlider={addBlurSlider}
              addZoomSlider={addZoomSlider}
              size={props.size}
            />
          );
        }
        return (
          <PanZoomImage
            boxes={props.boxes}
            disabled={props.hidden}
            size={props.size}
            src={props.attachment.content}
            caption={props.attachment.caption}
          />
        );
      }
      case TextCollectionAttachmentType.ImageGrid: {
        const images = props.attachment.content
          .split(',')
          .map(a => a.trim())
          .filter(a => a.length);
        const captions = (props.attachment.caption ?? '')
          .split(',')
          .map(a => a.trim())
          .filter(a => a.length);
        const columns = props.attachment.column_count ?? 3;
        return (
          <GridList
            style={{ height: '100%', width: '100%' }}
            cols={columns}
            cellHeight={75 + props.size.height / columns}
          >
            {images.map((image, index) => (
              <GridListTile key={index}>
                <BlurBrightnessImage
                  src={image}
                  caption={captions[index]}
                  addBrightnessSlider={addBrightnessSlider}
                  addBlurSlider={addBlurSlider}
                  addZoomSlider={addZoomSlider}
                  size={{
                    width: props.size.width / columns,
                    height: props.size.height / columns,
                  }}
                />
              </GridListTile>
            ))}
          </GridList>
        );
      }
      case TextCollectionAttachmentType.RLHFPairwiseEvaluation:
        return (
          <RLHFPairwiseEvaluation
            attachment={props.attachment}
            projectId={props.projectId}
            setAnnotations={props.setAnnotations}
          />
        );
      case TextCollectionAttachmentType.PairwiseEvaluation:
        return (
          <PairwiseEvaluation
            content={props.attachment.content}
            left={props.attachment.left!}
            right={props.attachment.right!}
          />
        );
      case TextCollectionAttachmentType.PDF:
        return (
          <PDFView
            src={props.attachment.content}
            renderWithPDFJs={props.projectConfig?.pdfAttachmentConfig?.renderWithPDFJs}
          />
        );
      case TextCollectionAttachmentType.GView:
        return <GView src={props.attachment.content} />;
      case TextCollectionAttachmentType.TranscriptionAudio:
        return (
          <TranscriptionAudioAttachmentView
            projectId={props.projectId}
            url={props.attachment.content}
            startSelfDestructSequence={props.startSelfDestructSequence}
            responseWindow={props.projectConfig?.audioAttachmentConfig?.responseWindow}
            audioPlaybackPrecision={
              props.projectConfig?.audioAttachmentConfig?.audioPlaybackPrecision
            }
          />
        );
      case TextCollectionAttachmentType.Audio:
        return (
          <Paper className={styles.textAttachment}>
            <AudioAttachmentView
              attachment={props.attachment}
              completed={props.completed}
              markAttachmentAsCompleted={props.markAttachmentAsCompleted}
              hidden={props.hidden}
              preventSkip={!props.projectConfig?.audioAttachmentConfig?.disablePreventSkip}
              startSelfDestructSequence={props.startSelfDestructSequence}
              responseWindow={props.projectConfig?.audioAttachmentConfig?.responseWindow}
              onFocus={onFocus}
              onBlur={onBlur}
              disabled={
                props.projectConfig?.audioAttachmentConfig?.disableConcurrentPlayback &&
                props.unfocused
              }
            />
          </Paper>
        );
      case TextCollectionAttachmentType.Video: {
        const defaultVideoComponent = (
          <>
            <Video
              srcVideoUrl={props.attachment.content}
              onLoadOrError={noop}
              caption={props.attachment.caption}
              onEnded={() => {
                props.markAttachmentAsCompleted(true);
              }}
              allowFileDownload={props.allowVideoAttachmentDownload}
              forceFullscreen={props.projectConfig?.videoAttachmentConfig?.enableForceFullscreen}
              onlyVisibleOnLoadComplete={
                props.projectConfig?.videoAttachmentConfig?.enableOnlyVisibleOnLoadComplete
              }
              preventSkip={!props.projectConfig?.videoAttachmentConfig?.disablePreventSkip}
              allowPlaybackRate={!props.projectConfig?.videoAttachmentConfig?.disablePlaybackRate}
              maximumExtraPlayingTime={
                props.projectConfig?.videoAttachmentConfig?.maximumExtraPlayingTime
              }
              disableThumbnail={props.projectConfig?.videoAttachmentConfig?.disableThumbnail}
              skipSourceVideoLoadingCheck={
                props.projectConfig?.videoAttachmentConfig?.skipSourceVideoLoadingCheck
              }
              disableVideoScaling={props.projectConfig?.videoAttachmentConfig?.disableVideoScaling}
              showCopyCurrentTimeButton={
                props.projectConfig?.videoAttachmentConfig?.showCopyCurrentTimeButton
              }
            />
          </>
        );
        const videoWithLiveInstructionsComponent = (
          <>
            <VideoWithLiveInstructions
              srcVideoUrl={props.attachment.content}
              onLoadOrError={noop}
              caption={props.attachment.caption}
              onEnded={() => {
                props.markAttachmentAsCompleted(true);
              }}
              allowFileDownload={props.allowVideoAttachmentDownload}
              forceFullscreen={props.projectConfig?.videoAttachmentConfig?.enableForceFullscreen}
              onlyVisibleOnLoadComplete={
                props.projectConfig?.videoAttachmentConfig?.enableOnlyVisibleOnLoadComplete
              }
              preventSkip={!props.projectConfig?.videoAttachmentConfig?.disablePreventSkip}
              allowPlaybackRate={!props.projectConfig?.videoAttachmentConfig?.disablePlaybackRate}
              maximumExtraPlayingTime={
                props.projectConfig?.videoAttachmentConfig?.maximumExtraPlayingTime
              }
              disableThumbnail={props.projectConfig?.videoAttachmentConfig?.disableThumbnail}
              skipSourceVideoLoadingCheck={
                props.projectConfig?.videoAttachmentConfig?.skipSourceVideoLoadingCheck
              }
              liveInstructions={props.liveInstructions}
            />
          </>
        );

        let videoComponentToReturn = defaultVideoComponent;
        if (useGridVideo) {
          videoComponentToReturn = (
            <VideoWithGrid
              url={props.attachment.content}
              onEnded={() => {
                props.markAttachmentAsCompleted(true);
              }}
            />
          );
        } else if (props.liveInstructions) {
          videoComponentToReturn = videoWithLiveInstructionsComponent;
        }

        return !props.disableVideoZoom ? (
          <svg tabIndex={0} onWheel={handleWheel} viewBox={viewbox} width="100%" height="100%">
            <foreignObject x="0" y="0" width={dimensions.width} height={dimensions.height}>
              {videoComponentToReturn}
            </foreignObject>
          </svg>
        ) : (
          videoComponentToReturn
        );
      }
      case TextCollectionAttachmentType.Subtask:
        return (
          <CategorizationEmbeddedTaskResponse
            id={props.attachment.content}
            attachmentBody={props.attachment.attachment_body}
          />
        );
      case TextCollectionAttachmentType.HTML:
        return <FrameView src={props.attachment.content} />;
      case TextCollectionAttachmentType.VideoComparison: {
        const videoComponent = (
          <VideoComparison
            srcVideoUrlA={props.attachment.videoA!}
            srcVideoUrlB={props.attachment.videoB!}
            onLoadOrError={noop}
            caption={props.attachment.caption}
            onEnded={() => {
              props.markAttachmentAsCompleted(true);
            }}
            allowFileDownload={props.allowVideoAttachmentDownload}
            forceFullscreen={props.projectConfig?.videoAttachmentConfig?.enableForceFullscreen}
            onlyVisibleOnLoadComplete={
              props.projectConfig?.videoAttachmentConfig?.enableOnlyVisibleOnLoadComplete
            }
            preventSkip={!props.projectConfig?.videoAttachmentConfig?.disablePreventSkip}
            allowPlaybackRate={!props.projectConfig?.videoAttachmentConfig?.disablePlaybackRate}
            maximumExtraPlayingTime={
              props.projectConfig?.videoAttachmentConfig?.maximumExtraPlayingTime
            }
            disableVideoScaling={props.projectConfig?.videoAttachmentConfig?.disableVideoScaling}
            disableThumbnail={props.projectConfig?.videoAttachmentConfig?.disableThumbnail}
          />
        );

        return videoComponent;
      }
      case TextCollectionAttachmentType.BulbaChatMobile: // to deprecate
      case TextCollectionAttachmentType.ChatMock:
        switch (props.attachment.mockType) {
          case ChatMockType.BulbaDesktop:
            return <BulbaChatDesktop attachment={props.attachment} />;
          case ChatMockType.ChatGPTDesktop:
            return <ChatGPTDesktop attachment={props.attachment} />;
          case ChatMockType.BulbaMobile:
          default:
            return <BulbaChatMobile attachment={props.attachment} />;
        }
      default:
        return (
          <Attachment
            attachmentType={props.attachment.type}
            attachment={props.attachment.content}
            onLoadOrError={noop}
            onOpenWebsite={() => {
              props.markAttachmentAsCompleted(true);
            }}
          />
        );
    }
  };
  return (
    <div
      ref={videoContainer}
      className={classNames(styles.attachment, {
        [styles.fixed]: !props.stacked,
        [styles.hidden]: props.hidden,
        [styles.sticky]: props.isSticky,
        [styles.imageAttachment]:
          props.attachment.type === TextCollectionAttachmentType.Image ||
          props.attachment.type === TextCollectionAttachmentType.Video ||
          props.attachment.type === TextCollectionAttachmentType.PDF,
      })}
    >
      {renderType()}
    </div>
  );
}

// hack for fair: add word count into attachment
const processTextForFair = (text: any) => {
  if (text.split('Baseline Response 1').length < 2) {
    return text;
  }

  const baselineRespLength = text.split('Baseline Response 1')[1].split(' ').length;
  const processedText = text.replace(
    'Baseline Response 1',
    `Baseline Response 1 (${baselineRespLength} words)`,
  );
  return processedText;
};

const processTextForBulba = (text: string, metadata: any): string | undefined => {
  const baselineResponse = _.get(metadata, 'BULBA_suggested_responses_edited_response[0]', '');
  if (!baselineResponse || !_.isString(baselineResponse)) return undefined;

  let processedText = text.split('\n').slice(0, -2).join('\n');

  processedText += `<div class='bot-msg'>
<div class='avatar'>
<div class='emoji'>🤖</div>
<div class='username'>Baseline Response</div>
</div>

<div class='msg-text'>
${baselineResponse}
</div>
</div>
</div>`;
  return processedText;
};

const fillVariables = async (text: string, loggedUserId: string) => {
  // can add more variables here in the future, e.g. current date or tasker email
  const variables: Record<string, string> = {
    TASKER_ID: loggedUserId,
    DOMAIN: 'outlier.ai',
  };
  return await populateTemplateHelper(text, variables);
};

interface TextComponentProps {
  props: AttachmentViewProps;
  disableLinksInTextAttachment?: boolean;
}

const TextComponent: React.FC<TextComponentProps> = ({
  props,
  disableLinksInTextAttachment,
}: TextComponentProps) => {
  const [text, setText] = useState<string>(props.attachment.content);

  // only supported for dolphin as of now, but can easily remove unleash in the future
  const useAttachmentTemplate = useUnleashFeatureFlag(
    'dolphin-customer-api-whitelist',
    { userId: props.taskCustomer },
    true,
  );
  const shouldReformatNewline = useUnleashFeatureFlag(
    'bulba-reformat-carriage-return',
    { projectId: props.projectId },
    false,
  );
  const enableBulbaBaselineResponse = useUnleashFeatureFlag(
    'enable-bulba-baseline-response',
    {},
    false,
  );
  const nightingaleReformat = useUnleashFeatureFlag(
    'nightingale-attachments-reformat',
    { projectId: props.projectId },
    false,
  );

  useAsync(async () => {
    if (!props.loggedUserId) return;

    // Fill in template variables
    let newText: any = undefined;
    if (useAttachmentTemplate) {
      newText = await fillVariables(props.attachment.content, props.loggedUserId);
    }

    // Postprocess text
    // for bulba
    if (
      enableBulbaBaselineResponse &&
      props.isReview &&
      _.isNumber(props.reviewLevel) &&
      props.reviewLevel >= 1 &&
      ((props.index === 1 && props.projectId === '64ac8ca60c5ea75a6df68084') ||
        props.projectId !== '64ac8ca60c5ea75a6df68084')
    ) {
      const maybeProcessedForBulba = processTextForBulba(
        props.attachment.content,
        props.subtaskMetadata,
      );
      if (maybeProcessedForBulba) {
        newText = maybeProcessedForBulba;
      }
    }
    // for flamingo
    if (props?.projectConfig?.pipelineConfig?.pipelineName === 'fair_eval_pipeline') {
      newText = processTextForFair(props.attachment.content);
    }
    if (newText && newText !== text) {
      setText(newText);
    }
  }, [
    useAttachmentTemplate,
    props.attachment.content,
    props.loggedUserId,

    enableBulbaBaselineResponse,
    props?.projectConfig?.pipelineConfig?.pipelineName,
    props.subtaskMetadata,
    props.index,
    props.isReview,
  ]);

  if (props.useProtectedText) {
    return (
      <ProtectedTextAttachment
        text={props.attachment.content}
        loggedUserId={props.loggedUserId}
        renderAsPlainText={props.projectConfig?.textAttachmentConfig?.renderAsPlainText}
        disableLinksInTextAttachment={disableLinksInTextAttachment}
      />
    );
  } else if (props.projectConfig?.textAttachmentConfig?.renderAsPlainText) {
    return <div>{props.attachment.content}</div>;
  } else {
    const maybeReformattedText = (() => {
      if (shouldReformatNewline) {
        return `${text
          .replaceAll('\\\\r', '\\r')
          .replaceAll('\\n', '\n')
          .replaceAll('<br />', '\n')}`;
      }
      if (nightingaleReformat) {
        return text.replaceAll('[', '\\[').replaceAll(']', '\\]');
      }
      return text;
    })();

    return (
      <SanitizedMarkdown
        text={maybeReformattedText}
        disableLinksInTextAttachment={disableLinksInTextAttachment}
        addBackgroundToTransparentImages
        handleLinkClickHistory={props.handleLinkClickHistory}
        addBrightnessSlider={props.projectConfig?.textAttachmentConfig?.addBrightnessSlider}
        addBlurSlider={props.projectConfig?.textAttachmentConfig?.addBlurSlider}
        escapeHtml={props.projectConfig?.textAttachmentConfig?.renderMarkdownWithoutLinks}
      />
    );
  }
};

const useAttachmentViewStyles = makeStyles({
  attachment: {
    width: '100%',
  },
  fixed: {
    height: '100%',
    overflowY: 'scroll',
  },
  hidden: {
    display: 'none',
  },
  sticky: {
    position: 'sticky',
    top: '0px',
    left: '0px',
  },
  chatbotTranscript: {
    height: 'calc(100vh - 420px)',
    overflowY: 'scroll',
    marginBottom: 16,
  },
  chatbotPrompt: {
    width: '100%',
    display: 'flex',
  },
  chatbotPromptButton: {
    marginLeft: 8,
    marginTop: 7,
  },
  textAttachment: {
    margin: 24,
    padding: 32,
    lineHeight: 1.6,
    '& tr': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
    },
    '& tr:nth-child(even)': {
      backgroundColor: '#f2f2f2',
    },
    '& th': {
      border: '1px solid rgb(0, 0, 0, 0.3)',
      padding: '6px 10px',
      backgroundColor: '#f2f2f2',
    },
    '& td': {
      border: '1px solid rgba(0, 0, 0, 0.3)',
      padding: '10px',
    },
  },
  imageAttachment: {
    height: '100%',
  },
});
